import type { PerformanceMetric, PerformancePoint } from './performance.types'

export const METRIC_LABEL: Record<PerformanceMetric, string> = {
  visibilityRate: 'Visibility Rate',
  weightedScorePercent: 'Weighted Score',
  shareOfVoice: 'Share of Voice',
}

const TIMESTAMP_FORMATTER = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'UTC',
})

export function formatPerformanceTimestamp(measurementAt: string): string {
  return `${TIMESTAMP_FORMATTER.format(new Date(measurementAt))} UTC`
}

// Private: only formatMetricPercent calls this, so the input is always already 0-100 scaled.
function formatNullablePercent(scaledValue: number | null): string {
  return scaledValue === null ? '—' : `${Math.round(scaledValue)}%`
}

// The weighted total is a float sum, so it needs rounding; one decimal keeps a sub-unit score from reading as zero.
const SCORE_FORMATTER = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 1,
})

export function formatWeightedRaw(
  total: number | null,
  max: number | null
): string {
  if (total === null || max === null) return '—'

  return `${SCORE_FORMATTER.format(total)} / ${SCORE_FORMATTER.format(max)}`
}

// Only visibility is rescaled for plotting; weighted percent and SOV pass through unchanged.
export function visibilityChartValue(point: PerformancePoint): number | null {
  return point.visibilityRate === null ? null : point.visibilityRate * 100
}

export function performanceMetricValue(
  point: PerformancePoint,
  metric: PerformanceMetric
): number | null {
  if (metric === 'visibilityRate') return visibilityChartValue(point)
  if (metric === 'weightedScorePercent') return point.weightedScorePercent

  return point.shareOfVoice
}

// Takes the raw point + metric, not a bare number, so callers can never pass the wrong scale.
export function formatMetricPercent(
  point: PerformancePoint,
  metric: PerformanceMetric
): string {
  return formatNullablePercent(performanceMetricValue(point, metric))
}
